/**
 * TOSS Web Application — Client portal: how a notification reads, in one place
 *
 * Two surfaces render the same feed and must not disagree about it: the full-screen page
 * (`features/client/**`) and the header popover (`components/layout/**`). Neither can import
 * from the other — the client-portal boundary (decision Р6) makes `features/client/**`
 * unimportable from outside, and its message says where shared logic belongs instead: here.
 *
 * What lives here is what both would otherwise copy — where a notification LEADS and how old
 * it reads. What does not: the markup. The page draws cards with room for the full text, the
 * popover draws dense rows; those are two designs on purpose, not one design duplicated.
 */

import { clientApplicationDetailsPath, clientChatPath } from '@/constants/routes';
import type { ClientNotificationDto } from '@/api/clientPortal/clientPortal.types';
import { formatRelativeAge } from '@/utils/date';

/**
 * Where the notification opens, or `null` when it opens nowhere.
 *
 * Follows the DTO: `applicationId` is the customer's read-only application card, and `chatId` is
 * the conversation the message was written in — named explicitly since a company can hold several.
 * It used to point at `/client/messenger` with no id, which was right while there was one chat and
 * became a link to the wrong conversation the moment there were two. A notification carrying
 * neither is informational; it must not be given a destination it does not have.
 */
export function clientNotificationTarget(
  notification: Pick<ClientNotificationDto, 'applicationId' | 'chatId'>
): string | null {
  if (notification.applicationId) return clientApplicationDetailsPath(notification.applicationId);
  if (notification.chatId) return clientChatPath(notification.chatId);
  return null;
}

/**
 * Age in words — the product's one implementation lives in `utils/date.ts`, shared with the
 * internal notifications panel since 2026-08-26. Kept as a named re-export because the two
 * portal surfaces call it by this name in a dozen places, and the name says which feed it reads.
 */
export const formatClientNotificationAge = formatRelativeAge;
